import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ITenantBranding } from '@carrierbridge/shared';
import api, { getAccessToken } from '@/lib/api';

const BRANDING_KEY = ['branding'] as const;

/**
 * Marque du tenant. **Ne part pas sans session** : les pages publiques
 * (portail de cotation, suivi d'ordre, coffre documentaire) sont ouvertes par
 * un transporteur sans compte, pour qui il n'y a pas de tenant — l'appel n'y
 * rapportait qu'un 401 inutile. Elles retombent sur la marque par défaut.
 */
export function useBranding() {
  return useQuery<ITenantBranding | null>({
    queryKey: BRANDING_KEY,
    queryFn: async () => {
      const { data } = await api.get<ITenantBranding | null>('/branding');
      return data;
    },
    enabled: !!getAccessToken(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { logoUrl?: string; brandName?: string; accentColor?: string }) => {
      const { data } = await api.patch<ITenantBranding>('/branding', dto);
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(BRANDING_KEY, data);
    },
  });
}

export function useUploadLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post<{ logoUrl: string }>('/branding/logo', formData);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: BRANDING_KEY });
    },
  });
}
